import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAnalysis } from "../contexts/AnalysisContext";
import { CheckCircle, WarningCircle, X, ArrowRight } from "@phosphor-icons/react";

/**
 * Tiny floating widget pinned to the bottom-left of every page that shows the
 * current bank-statement analysis state — so the user can navigate freely while
 * Gemini chews through their PDFs.
 */
export default function FloatingAnalysisStatus() {
  const { status, phase, elapsed, parsed, recs, files, cancel, reset, isRunning } = useAnalysis();
  const nav = useNavigate();
  const loc = useLocation();

  // Hide on the pricing page itself (where the full status card is already shown)
  if (loc.pathname.startsWith("/pricing")) return null;
  if (status === "idle") return null;

  const fileCount = Math.max(1, files.length);
  const expected = 20 + 15 * fileCount;
  const pct = Math.min(0.95, elapsed / Math.max(1, expected));
  const radius = 18, stroke = 4;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - pct);
  const overrun = elapsed > expected;

  const isComplete = status === "complete";
  const isFailed = status === "failed";

  return (
    <div
      data-testid="floating-analysis"
      className="fixed bottom-5 left-5 z-40 w-72 rounded-lg shadow-2xl bg-slate-900 text-white overflow-hidden animate-in fade-in slide-in-from-bottom-2"
    >
      <div className="px-4 py-3 flex items-center gap-3 border-b border-slate-700">
        {isRunning && (
          <div className="relative shrink-0" style={{ width: 44, height: 44 }}>
            <svg width={44} height={44} className="-rotate-90">
              <circle cx={22} cy={22} r={radius} stroke="#334155" strokeWidth={stroke} fill="none" />
              <circle
                cx={22} cy={22} r={radius}
                stroke={overrun ? "#f59e0b" : "#22d3ee"}
                strokeWidth={stroke} fill="none" strokeLinecap="round"
                strokeDasharray={circumference} strokeDashoffset={offset}
                style={{ transition: "stroke-dashoffset 0.8s linear, stroke 0.3s" }}
              />
            </svg>
            <div className="absolute inset-0 grid place-items-center text-[10px] font-mono font-bold">
              {overrun ? `${elapsed}s` : `${Math.max(0, expected - elapsed)}s`}
            </div>
          </div>
        )}
        {isComplete && <CheckCircle size={28} weight="fill" className="text-emerald-400 shrink-0" />}
        {isFailed && <WarningCircle size={28} weight="fill" className="text-red-400 shrink-0" />}

        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-400">Bank statement analysis</div>
          <div className="text-sm font-semibold truncate">
            {isRunning && (phase || "Working…")}
            {isComplete && (parsed?.error ? "Finished with warnings" : "Report ready")}
            {isFailed && "Analysis failed"}
          </div>
          {isComplete && parsed && !parsed.error && (
            <div className="text-[11px] text-slate-300 mt-0.5">
              {parsed.mca_position_count ?? 0} MCAs · {recs?.length || 0} lender matches
            </div>
          )}
        </div>

        <button
          onClick={isRunning ? cancel : reset}
          title={isRunning ? "Cancel" : "Dismiss"}
          className="h-7 w-7 grid place-items-center rounded-md hover:bg-white/10 text-slate-400 hover:text-white shrink-0"
        >
          <X size={14} />
        </button>
      </div>
      <button
        onClick={() => nav("/pricing")}
        className="w-full px-4 py-2 text-xs font-semibold text-cyan-300 hover:bg-slate-800 inline-flex items-center justify-center gap-1.5 uppercase tracking-[0.15em]"
        data-testid="floating-analysis-open"
      >
        {isComplete ? "View report" : "Open analyzer"} <ArrowRight size={12} />
      </button>
    </div>
  );
}
